// UsageManager.js - Admin panel for account signups, per-user activity, and the
// event/error stream (GET /api/admin/usage). Lists every account, including
// silent ones: an account with no projects and no activity is the signal.
import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import THEME from '../../styles/theme';
import api from '../../utils/api';

const WINDOWS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 0, label: 'All time' }
];

const USER_COLUMNS = '1.4fr 1.3fr 1.3fr 0.7fr 0.7fr 0.7fr 0.9fr 1.3fr';
const ACTIVITY_COLUMNS = '0.6fr 1.2fr 1fr 2.6fr 1.3fr';

const TUTORIAL_LABELS = {
  tutorial_start: 'started',
  tutorial_pause: 'paused',
  tutorial_skip: 'skipped',
  tutorial_complete: 'completed'
};

const formatWhen = (value) => (value ? new Date(value).toLocaleString() : '—');
const formatAction = (action) => action.replace(/_/g, ' ');

// Collapse consecutive identical rows (same user, kind, action, detail) so an
// error storm reads as one line with a count instead of burying everything else.
const collapseRuns = (rows) => {
  const out = [];
  for (const row of rows) {
    const prev = out[out.length - 1];
    if (prev && prev.user_id === row.user_id && prev.kind === row.kind &&
        prev.action === row.action && prev.detail === row.detail) {
      prev.count += 1;
      prev.first_at = row.created_at;
    } else {
      out.push({ ...row, count: 1, first_at: row.created_at });
    }
  }
  return out;
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -45%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${THEME.zIndex.modal};
  animation: ${fadeIn} ${THEME.transitionFast};
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${THEME.panelBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: 16px;
  width: 95%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: ${slideUp} ${THEME.transitionMedium};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${THEME.spacing.lg} ${THEME.spacing.xl};
  border-bottom: 1px solid ${THEME.inputBorder};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${THEME.fontSize.xl};
  color: ${THEME.textPrimary};
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xl};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all ${THEME.transitionFast};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${THEME.textPrimary};
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: ${THEME.spacing.md};
  padding: ${THEME.spacing.md} ${THEME.spacing.xl};
  border-bottom: 1px solid ${THEME.inputBorder};
  flex-wrap: wrap;
  align-items: center;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: ${THEME.spacing.xs};
`;

const FilterButton = styled.button`
  padding: ${THEME.spacing.xs} ${THEME.spacing.md};
  background: ${props => props.$active ? THEME.accentPrimary : 'transparent'};
  color: ${props => props.$active ? 'white' : THEME.textSecondary};
  border: 1px solid ${props => props.$active ? THEME.accentPrimary : THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.sm};
  cursor: pointer;
  transition: all ${THEME.transitionFast};

  &:hover {
    border-color: ${THEME.accentPrimary};
    color: ${props => props.$active ? 'white' : THEME.textPrimary};
  }
`;

const SearchInput = styled.input`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.xs} ${THEME.spacing.md};
  color: ${THEME.inputText};
  font-size: ${THEME.fontSize.sm};
  width: 220px;

  &:focus {
    outline: none;
    border-color: ${THEME.inputBorderFocus};
  }

  &::placeholder {
    color: ${THEME.textMuted};
  }
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.xs};
  font-size: ${THEME.fontSize.sm};
  color: ${THEME.textSecondary};
  cursor: pointer;
  margin-left: auto;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: ${THEME.spacing.lg};
`;

const Section = styled.div`
  padding: 0 ${THEME.spacing.xl};
`;

const SectionTitle = styled.h3`
  margin: ${THEME.spacing.lg} 0 ${THEME.spacing.sm};
  font-size: ${THEME.fontSize.sm};
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${THEME.textSecondary};
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${THEME.spacing.md};
`;

const Tile = styled.div`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusMedium};
  padding: ${THEME.spacing.md};
`;

const TileValue = styled.div`
  font-size: ${THEME.fontSize.xxl};
  font-weight: 700;
  color: ${props => props.$alert ? THEME.accentPrimary : THEME.textPrimary};
`;

const TileLabel = styled.div`
  font-size: ${THEME.fontSize.sm};
  color: ${THEME.textMuted};
  margin-top: ${THEME.spacing.xs};
`;

const Table = styled.div`
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusMedium};
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$columns};
  gap: ${THEME.spacing.sm};
  align-items: center;
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  font-size: ${THEME.fontSize.md};
  border-bottom: 1px solid ${THEME.inputBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const HeaderRow = styled(Row)`
  background: rgba(255, 255, 255, 0.03);
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.xs};
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const Cell = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${props => props.$warn ? THEME.warning : (props.$muted ? THEME.textSecondary : THEME.textPrimary)};
  font-weight: ${props => props.$warn ? 600 : 'inherit'};
`;

const KindBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.xs};
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${props => props.$kind === 'error' ? THEME.accentPrimary : 'rgba(255, 255, 255, 0.08)'};
  color: ${props => props.$kind === 'error' ? 'white' : THEME.textSecondary};
`;

const CountBadge = styled.span`
  margin-left: ${THEME.spacing.xs};
  color: ${THEME.warning};
  font-weight: 600;
`;

const AdminTag = styled.span`
  margin-left: ${THEME.spacing.xs};
  font-size: ${THEME.fontSize.xs};
  color: ${THEME.textMuted};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${THEME.spacing.xl};
  color: ${THEME.textSecondary};
`;

const StatusLine = styled.div`
  padding: ${THEME.spacing.md} ${THEME.spacing.xl};
  color: ${props => props.$error ? THEME.accentPrimary : THEME.textSecondary};
`;

const Footnote = styled.div`
  padding: ${THEME.spacing.lg} ${THEME.spacing.xl} 0;
  font-size: ${THEME.fontSize.xs};
  color: ${THEME.textMuted};
  line-height: 1.5;
`;

const UsageManager = ({ isOpen, onClose }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getAdminUsage(days));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (isOpen) {
      load();
    }
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const query = search.trim().toLowerCase();
  const matches = (...fields) => !query || fields.some(f => String(f ?? '').toLowerCase().includes(query));

  const users = (data?.users || []).filter(u => matches(u.username));
  const activity = collapseRuns(
    (data?.activity || []).filter(a =>
      (!errorsOnly || a.kind === 'error') && matches(a.username, a.action, a.detail)
    )
  );
  const tiles = data?.tiles || {};
  const windowLabel = days === 0 ? 'all time' : `last ${days} days`;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Usage &amp; Activity</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close">✕</CloseButton>
        </ModalHeader>

        <FilterBar>
          <FilterGroup>
            {WINDOWS.map(w => (
              <FilterButton key={w.days} $active={days === w.days} onClick={() => setDays(w.days)}>
                {w.label}
              </FilterButton>
            ))}
          </FilterGroup>
          <FilterButton onClick={load} disabled={loading}>↻ Refresh</FilterButton>
          <SearchInput
            type="text"
            placeholder="Filter by user, action, detail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Toggle>
            <input
              type="checkbox"
              checked={errorsOnly}
              onChange={(e) => setErrorsOnly(e.target.checked)}
            />
            Errors only
          </Toggle>
        </FilterBar>

        <Body>
          {error && <StatusLine $error>{error}</StatusLine>}
          {loading && !data && <StatusLine>Loading usage data...</StatusLine>}

          {data && (
            <>
              <Section>
                <SectionTitle>Summary ({windowLabel})</SectionTitle>
                <Tiles>
                  <Tile>
                    <TileValue>{tiles.new_accounts}</TileValue>
                    <TileLabel>New accounts</TileLabel>
                  </Tile>
                  <Tile>
                    <TileValue>{tiles.active_users}</TileValue>
                    <TileLabel>Active users</TileLabel>
                  </Tile>
                  <Tile>
                    <TileValue>{tiles.saves}</TileValue>
                    <TileLabel>Project saves</TileLabel>
                  </Tile>
                  <Tile>
                    <TileValue $alert={tiles.errors > 0}>{tiles.errors}</TileValue>
                    <TileLabel>Errors</TileLabel>
                  </Tile>
                  <Tile>
                    <TileValue>{tiles.total_users}</TileValue>
                    <TileLabel>Total accounts (all time)</TileLabel>
                  </Tile>
                </Tiles>
              </Section>

              <Section>
                <SectionTitle>Accounts ({users.length})</SectionTitle>
                <Table>
                  <HeaderRow $columns={USER_COLUMNS}>
                    <Cell>User</Cell>
                    <Cell>Joined</Cell>
                    <Cell>Last login</Cell>
                    <Cell>Projects</Cell>
                    <Cell>Events</Cell>
                    <Cell>Errors</Cell>
                    <Cell>Tutorial</Cell>
                    <Cell>Last activity</Cell>
                  </HeaderRow>
                  {users.length === 0 && <EmptyState>No accounts match</EmptyState>}
                  {users.map(u => (
                    <Row key={u.id} $columns={USER_COLUMNS}>
                      <Cell title={`user id ${u.id}`}>
                        {u.username}
                        {u.is_admin && <AdminTag>admin</AdminTag>}
                      </Cell>
                      <Cell $muted>{formatWhen(u.created_at)}</Cell>
                      <Cell $muted>{formatWhen(u.last_login)}</Cell>
                      <Cell $warn={u.project_count === 0} title={u.project_count === 0 ? 'Never saved a project' : `Last project update ${formatWhen(u.last_project_update)}`}>
                        {u.project_count}
                      </Cell>
                      <Cell>{u.events}</Cell>
                      <Cell $warn={u.errors > 0}>{u.errors}</Cell>
                      <Cell $muted>{u.tutorial_state ? TUTORIAL_LABELS[u.tutorial_state] : '—'}</Cell>
                      <Cell $muted>{formatWhen(u.last_activity)}</Cell>
                    </Row>
                  ))}
                </Table>
              </Section>

              <Section>
                <SectionTitle>Recent activity ({activity.length}{errorsOnly ? ', errors only' : ''})</SectionTitle>
                <Table>
                  <HeaderRow $columns={ACTIVITY_COLUMNS}>
                    <Cell>Kind</Cell>
                    <Cell>Action</Cell>
                    <Cell>User</Cell>
                    <Cell>Detail</Cell>
                    <Cell>When</Cell>
                  </HeaderRow>
                  {activity.length === 0 && <EmptyState>No activity in this window</EmptyState>}
                  {activity.map(a => (
                    <Row key={a.id} $columns={ACTIVITY_COLUMNS}>
                      <Cell><KindBadge $kind={a.kind}>{a.kind}</KindBadge></Cell>
                      <Cell title={`reported by ${a.source}`}>
                        {formatAction(a.action)}
                        {a.count > 1 && <CountBadge title={`${a.count} identical rows, ${formatWhen(a.first_at)} to ${formatWhen(a.created_at)}`}>×{a.count}</CountBadge>}
                      </Cell>
                      <Cell $muted>{a.username || '—'}</Cell>
                      <Cell $muted title={a.detail || ''}>{a.detail || '—'}</Cell>
                      <Cell $muted>{formatWhen(a.created_at)}</Cell>
                    </Row>
                  ))}
                </Table>
              </Section>

              <Footnote>
                Stream shows the newest 200 rows in the window; consecutive identical rows are collapsed with a count.
                Projects in yellow have never been saved. Activity logging began 2026-09-21, so earlier usage is
                visible only through Joined, Last login, and Projects.
              </Footnote>
            </>
          )}
        </Body>
      </Modal>
    </Overlay>
  );
};

export default UsageManager;
